import _ from 'lodash';
import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import i18n from '../../config/i18n';
import WoundLocationImages from '../../constants/WoundLocationImages';
import WoundAssessment from './WoundAssessment';
import Link from '../shared/Link';
import SingleLineChart from '../../components/shared/SingleLineChart';
import {
  DOCTOR_ID,
  ASSISTANT_ID,
  NURSE_ID,
} from '../../constants/RoleConstants';
import { WOUND_PDF } from '../../constants/exportPDFTypes';

// TODO: convert this to import safely
const styles = {
  wrapper: {
    overflowX: 'auto',
  },
  table: {
    marginTop: 20,
  },
  thead: {
    backgroundColor: '#F1E9DB',
    color: '#2D3E4F',
  },
  th: {
    fontSize: 20,
  },
  edit: {
    float: 'right',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
  image: {
    maxHeight: 300,
    margin: 'auto',
  },
  label: {
    textAlign: 'center',
    backgroundColor: '#922E3A',
    color: 'white',
  },
};


const WoundCondition = ({
  actions,
  assessments,
  id,
  injuryDate,
  location,
  medicalRecordId,
  metadata,
  metadata: {
    data: {
      woundType,
      woundLocation,
    },
  },
  onExportPDF,
  router,
  type,
  user,
  user: { profile: { roleId } },
}) => {
  const [showExtra, setShowExtra] = useState(false);
  const [data, setData] = useState([]);
  const targetWoundType = _.filter(woundType, { id: parseInt(type, 10) });
  const targetWoundLocation = _.filter(woundLocation, { id: parseInt(location, 10) });

  useEffect(() => {
    assessments.sort((a, b) => moment(a.created).valueOf() - moment(b.created).valueOf());
    const labels = assessments.map(a =>
      `${momentTimezone(a.created).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mma')} (PST)`);
    const woundData = assessments.map(a => a.width * a.height);
    assessments.sort((a, b) => moment(b.created).valueOf() - moment(a.created).valueOf());
    const localData = labels.map((label, idx) => ({
      date: label,
      score: woundData[idx],
    }));

    setData(localData);
  }, [assessments]);

  const expand = () => { setShowExtra(!showExtra); };

  const canAddAssessment = () => ([DOCTOR_ID, ASSISTANT_ID, NURSE_ID].indexOf(roleId) > -1);

  const canExportPdf = () => ([DOCTOR_ID, ASSISTANT_ID, NURSE_ID].indexOf(roleId) > -1);

  const handleExportPdfClick = async () => {
    if (await actions.checkExportPDFPermission()) {
      onExportPDF(id, WOUND_PDF);
    }
  };

  const {
    link,
    table,
    wrapper,
    thead,
    th,
    edit,
    image,
    label,
  } = styles;

  return (
    <div style={wrapper} className="WoundCondition">
      <table className="table table-bordered table-responsive" style={table}>
        <thead style={thead}>
          <tr>
            <th colSpan="3" style={th}>
              {i18n.t('woundAndSkinConditions')}
              <div className="btn-group" style={edit}>
                {
                  assessments.length !== 0 && (
                    <div className="btn-group">
                      {
                        canAddAssessment() && (
                          <Link
                            href={`/medical-records/${medicalRecordId}/wounds/${id}/assessment-create`}
                            style={link}
                            router={router}
                          >
                            <button
                              className="btn btn-sm btn-success"
                              title="add assessment"
                            >
                              {i18n.t('addAssessment')}
                            </button>
                          </Link>
                        )
                      }
                    </div>
                  )
                }
                {
                  (assessments.length !== 0 && canExportPdf()) && (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={handleExportPdfClick}
                    >
                      {i18n.t('exportPDF')}
                    </button>
                  )
                }
                {
                  assessments.length !== 0 && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={expand}
                    >
                      {i18n.t(showExtra ? 'collapse' : 'expand')}
                    </button>
                  )
                }
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td width="20%" rowSpan="4">
              <img
                style={image}
                className="img-responsive"
                src={WoundLocationImages[_.get(woundLocation, ['0', 'code'])]}
                alt={i18n.t('woundLocation')}
              />
            </td>
            <th width="20%">
              {i18n.t('injuryDate')}:
            </th>
            <td width="40%">
              {i18n.t('injuryDate_pst', {
                injuryDate: momentTimezone(injuryDate).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mma'),
              })}
            </td>
          </tr>

          <tr>
            <th>
              {i18n.t('daysSinceInjuryDate')}:
            </th>
            <td>
              {i18n.t('injuryDays', {
                days: moment().diff(moment(injuryDate), 'days'),
              })}
            </td>
          </tr>
          <tr>
            <th>
              {i18n.t('woundLocation')}:
            </th>
            <td>
              {targetWoundLocation[0] ? targetWoundLocation[0].name : ''}
            </td>
          </tr>
          <tr>
            <th>
              {i18n.t('woundType')}:
            </th>
            <td>
              {targetWoundType[0] ? targetWoundType[0].name : ''}
            </td>
          </tr>
          {
            !assessments.length && canAddAssessment() ? (
              <tr>
                <td
                  colSpan="3"
                  style={label}
                >
                  <Link
                    href={`/medical-records/${medicalRecordId}/wounds/${id}/assessment-create`}
                    style={link}
                    router={router}
                  >
                    {i18n.t('noAssessmentsMsg')}:
                  </Link>
                </td>
              </tr>
            ) : null
          }
        </tbody>
      </table>
      {
        showExtra && !_.isEmpty(assessments) && [
          <SingleLineChart
            height={200}
            data={data}
            noTitle
            key="assessmentGraph"
          />,
          ...assessments.map(assessment => (
            <WoundAssessment
              {...assessment}
              key={assessment.id}
              actions={actions}
              metadata={metadata}
              user={user}
            />
          )),
        ]
      }
    </div>
  );
};

WoundCondition.propTypes = {
  actions: PropTypes.shape({
    checkExportPDFPermission: PropTypes.func.isRequired,
  }).isRequired,
  assessments: PropTypes.arrayOf(PropTypes.object),
  id: PropTypes.number.isRequired,
  injuryDate: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  medicalRecordId: PropTypes.number.isRequired,
  metadata: PropTypes.shape({
    data: PropTypes.shape({
      woundType: PropTypes.arrayOf(PropTypes.object),
      woundLocation: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
  }).isRequired,
  onExportPDF: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  user: PropTypes.shape({
    profile: PropTypes.shape({
      roleId: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

WoundCondition.defaultProps = {
  assessments: [],
};

export default WoundCondition;



// WEBPACK FOOTER //
// ./src/components/patient/WoundCondition.js